/**
 * Composable for fetching social media information for a specific brand.
 *
 * @param {ComputedRef<string>} brand - A computed reference to the brand identifier.
 * @returns {Ref<any>} A reference containing the brand's social media data.
 *
 * @example
 * // Usage in a Vue component
 * const brand = computed(() => 'example-brand')
 * const brandSocials = useBrandSocials(brand)
 *
 * @description
 * This composable fetches social media information for a given brand using the CMS API.
 * It creates a unique delivery key based on the brand and uses Nuxt's useFetch for data retrieval.
 *
 * @see useFetch - Nuxt's data fetching composable
 * @see useLocalePath - Nuxt's localization path utility
 */
export const useBrandSocials = (brand: ComputedRef<string>) => {
  const deliveryKey = computed(() => 'brand-socials-' + brand.value)

  const localePath = useLocalePath()

  const { data } = useFetch('/api/cms/getBrandSocials', {
    query: {
      locale: localePath(''),
      deliveryKey: deliveryKey,
    },
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
    dedupe: 'defer',
    watch: [deliveryKey],
  })

  return data
}
